import { useBlocker } from "@tanstack/react-router";
import {
  type Dispatch,
  type RefObject,
  type SetStateAction,
  useEffect,
  useRef,
} from "react";
import type { TeamsUsersResponse } from "../../queries/useTeamsUsersQuery";
import type { ProcedureAuthoringDraft } from "./procedureAuthoring";

export function useProcedureAuthoringDefaults({
  data,
  draft,
  procedureId,
  setDraft,
}: {
  data: TeamsUsersResponse | undefined;
  draft: ProcedureAuthoringDraft;
  procedureId?: string;
  setDraft: Dispatch<SetStateAction<ProcedureAuthoringDraft>>;
}) {
  useEffect(() => {
    if (!data) return;
    const owner = data.users.find(
      (candidate) => candidate.name === draft.owner,
    );
    const currentUser = data.currentUserId
      ? data.users.find((candidate) => candidate.id === data.currentUserId)
      : undefined;
    const defaultOwner =
      !procedureId && !draft.ownerId && !draft.owner.trim()
        ? currentUser
        : undefined;
    const team = data.teams.find((candidate) => candidate.name === draft.team);
    const ownerId = draft.ownerId ?? owner?.id ?? defaultOwner?.id ?? null;
    const teamId = draft.teamId ?? team?.id ?? null;
    const ownerName = defaultOwner?.name ?? draft.owner;
    if (
      draft.ownerId !== ownerId ||
      draft.owner !== ownerName ||
      draft.teamId !== teamId
    ) {
      setDraft((current) => ({
        ...current,
        owner: current.owner.trim() || defaultOwner?.name || current.owner,
        ownerId: current.ownerId ?? ownerId,
        teamId: current.teamId ?? teamId,
      }));
    }
  }, [
    data,
    draft.owner,
    draft.ownerId,
    draft.team,
    draft.teamId,
    procedureId,
    setDraft,
  ]);
}

export function useProcedureAuthoringLoadedDraft({
  data,
  isDirty,
  setDraft,
  setSavedSnapshot,
  setSelectedItemId,
  flattenItems,
}: {
  data: ProcedureAuthoringDraft | undefined;
  isDirty: boolean;
  setDraft: Dispatch<SetStateAction<ProcedureAuthoringDraft>>;
  setSavedSnapshot: (value: string) => void;
  setSelectedItemId: Dispatch<SetStateAction<string | null>>;
  flattenItems: (draft: ProcedureAuthoringDraft) => { id: string }[];
}) {
  const lastLoadedData = useRef<ProcedureAuthoringDraft | undefined>(undefined);
  useEffect(() => {
    if (!data || isDirty || data === lastLoadedData.current) return;
    lastLoadedData.current = data;
    setDraft(data);
    setSavedSnapshot(JSON.stringify(data));
    setSelectedItemId(flattenItems(data)[0]?.id ?? null);
  }, [
    data,
    flattenItems,
    isDirty,
    setDraft,
    setSavedSnapshot,
    setSelectedItemId,
  ]);
}

export function useProcedureAuthoringNavigationWarning({
  isDirty,
  onBlocked,
  skipNextNavigation,
}: {
  isDirty: boolean;
  onBlocked: (resolve: (allowNavigation: boolean) => void) => void;
  skipNextNavigation: RefObject<boolean>;
}) {
  useBlocker({
    shouldBlockFn: async () => {
      if (skipNextNavigation.current) {
        skipNextNavigation.current = false;
        return false;
      }
      if (!isDirty) return false;
      return new Promise<boolean>((resolve) => onBlocked(resolve));
    },
    enableBeforeUnload: isDirty,
    disabled: !isDirty,
  });

  useEffect(() => {
    if (!isDirty) return;
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty]);
}

export function useProcedureAuthoringFocus({
  focusNewItemId,
  focusNewStructure,
  selectedItemId,
  selectedStructure,
}: {
  focusNewItemId: RefObject<string | null>;
  focusNewStructure: RefObject<{
    kind: "heading" | "section";
    id: string;
  } | null>;
  selectedItemId: string | null;
  selectedStructure: { kind: "heading" | "section"; id: string } | null;
}) {
  useEffect(() => {
    if (!focusNewItemId.current || focusNewItemId.current !== selectedItemId)
      return;
    document.getElementById(`procedure-item-label-${selectedItemId}`)?.focus();
    focusNewItemId.current = null;
  }, [focusNewItemId, selectedItemId]);

  useEffect(() => {
    void selectedStructure;
    const target = focusNewStructure.current;
    if (!target) return;
    document.getElementById(`procedure-${target.kind}-${target.id}`)?.focus();
    focusNewStructure.current = null;
  }, [focusNewStructure, selectedStructure]);
}
